// The fact kind registry: one Zod schema per `fact.kind`.
//
// `fact.payload_json` is the one column in the schema that the database cannot
// check. Without this registry, "validate at the boundary with Zod" stops at
// the edge of the fact table and everything inside is whatever an adapter
// happened to emit — which is exactly the shape of problem that took three
// weeks to notice with Sleeper.
//
// A kind is registered here or it cannot be written. That is deliberate: a new
// fact kind should be a decision somebody made, not a string that appeared in
// an adapter.
//
// Versioning, per ICD-S07 §13: a kind's schema may add OPTIONAL fields freely.
// A breaking change mints a new kind rather than redefining an old one, because
// facts are append-only and history has to keep parsing.
//
// NO SPORT OR VENDOR WORD IN A FIELD NAME. Decision A12, 2026-09-17. A payload
// shape here is stored, so a field called `footballName` or `sleeperStatus`
// outlives the day somebody notices — it is in every row already written, in
// every reader, and in the JSON Schema a client generated from it. The names
// are for the SHAPE ("the name they go by", "the source's own total"), never
// for the sport or the provider that happened to supply the first one.
//
// What the rule does NOT mean: a CODED VALUE stays the source's word verbatim —
// `position: 'CB'`, `scheme: 'ppr'` — because the whole point of the fact table
// is being able to answer "what did the source actually say". Vocabulary is
// data and is mapped by `concept_map`; a field name is structure and is not.
//
// Two shapes are deliberately exempt and say so where they are defined:
// `VenueLocation.nwsCovered` and the whole `venue.gridpoint` kind are about the
// US National Weather Service specifically, and naming them generically would
// claim a generality they do not have. They get a SIBLING when a second weather
// provider arrives, not a rename.

import { z } from 'zod';

/** Shared across every kind that is about a scheduled contest. */
const ContestRef = {
  gameId: z.string().min(1),
  season: z.number().int(),
  period: z.number().int().nullable(),
  home: z.string().min(1),
  away: z.string().min(1),
};

/**
 * A fixture, as planned.
 *
 * Nullable is used throughout rather than optional, and the difference matters:
 * `null` means the source had nothing to say, and a missing key would mean the
 * adapter forgot. Requiring the key forces an adapter to answer.
 */
export const ContestScheduled = z.object({
  ...ContestRef,
  gameType: z.string().nullable(),
  /** An instant built from the local parts below. See the adapter for why. */
  kickoff: z.string().nullable(),
  localDate: z.string().nullable(),
  localTime: z.string().nullable(),
  weekday: z.string().nullable(),
  /**
   * The zone the three fields above are on — `America/New_York` for nflverse.
   *
   * Optional because facts written before 2026-09-13 do not carry it, and the
   * read path validates: making it required would make every existing contest
   * unreadable until the next extraction superseded it. Adding an OPTIONAL
   * field is the compatible change ICD-S07 §13 allows.
   */
  sourceZone: z.string().optional(),
  location: z.string().nullable(),
  divisional: z.boolean().nullable(),
  venue: z.string().nullable(),
  venueName: z.string().nullable(),
  roof: z.string().nullable(),
  surface: z.string().nullable(),
  /** Days since each side last played. How "after a bye" is known. */
  homeRest: z.number().nullable(),
  awayRest: z.number().nullable(),
  /**
   * Carried so a reader can tell inapplicable weather from unrecorded weather.
   * nflverse leaves temp and wind blank for both cases.
   */
  indoors: z.boolean(),
  temp: z.number().nullable(),
  wind: z.number().nullable(),
  referee: z.string().nullable(),
  homeCoach: z.string().nullable(),
  awayCoach: z.string().nullable(),
  spreadLine: z.number().nullable(),
  totalLine: z.number().nullable(),
  homeMoneyline: z.number().nullable(),
  awayMoneyline: z.number().nullable(),
});

/** A final score. A contest with no result has no fact, never a null score. */
export const ContestResult = z.object({
  ...ContestRef,
  homeScore: z.number().int(),
  awayScore: z.number().int(),
  margin: z.number().int(),
  total: z.number().int(),
  overtime: z.boolean().nullable(),
});

/** Shared across every kind that is about a person in a given week. */
const PersonWeek = {
  personKey: z.string().min(1),
  season: z.number().int(),
  period: z.number().int(),
  team: z.string().min(1),
};

/**
 * Who somebody is.
 *
 * Every coded value here is the SOURCE's word, verbatim — `position: 'CB'`,
 * `status: 'RES'` — never our vocabulary. Translating at write time would make
 * the fact table unable to answer "what did nflverse actually say", which is
 * the question a drift investigation starts from. The mapping to our own terms
 * is a `concept_map` row, and it can change without touching a single fact.
 */
export const PersonProfile = z.object({
  personKey: z.string().min(1),
  /** Present only when the source's key really is a GSIS id. See namespaces.ts. */
  gsisId: z.string().nullable(),
  displayName: z.string().min(1),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  /** "P.Mahomes". Null means the sport derives it. */
  shortName: z.string().nullable(),
  /**
   * The name they are known by where it differs from the given name — "Mitch"
   * for a Mitchell, "Rob" for a Robert. Null where the source has nothing.
   *
   * Was `footballName` until 2026-09-18, which is nflverse's column name for
   * it. The field is about a PERSON, not about a sport, and a stored shape that
   * says otherwise is wrong in every row it has ever been written to.
   */
  knownAs: z.string().nullable(),
  suffix: z.string().nullable(),
  position: z.string().nullable(),
  positionGroup: z.string().nullable(),
  status: z.string().nullable(),
  statusDescription: z.string().nullable(),
  birthDate: z.string().nullable(),
  heightInches: z.number().nullable(),
  weightPounds: z.number().nullable(),
  college: z.string().nullable(),
  collegeConference: z.string().nullable(),
  jerseyNumber: z.number().nullable(),
  rookieSeason: z.number().nullable(),
  lastSeason: z.number().nullable(),
  latestTeam: z.string().nullable(),
  yearsExperience: z.number().nullable(),
  draftYear: z.number().nullable(),
  draftRound: z.number().nullable(),
  draftPick: z.number().nullable(),
  draftTeam: z.string().nullable(),
  headshot: z.string().nullable(),
});

/** Who somebody played for, in one week. The fact "whose team?" is answered from. */
export const RosterMembership = z.object({
  ...PersonWeek,
  status: z.string().nullable(),
  statusDetail: z.string().nullable(),
  position: z.string().nullable(),
  depthPosition: z.string().nullable(),
  jerseyNumber: z.number().nullable(),
  yearsExperience: z.number().nullable(),
  gameType: z.string().nullable(),
});

/**
 * One line of the official participation report.
 *
 * The two statuses stay separate on purpose. Questionable-having-practised-fully
 * and Questionable-having-not-practised are different situations that a single
 * merged field cannot express, and merging them is a decision that cannot be
 * undone later from the stored row.
 */
export const InjuryReport = z.object({
  ...PersonWeek,
  position: z.string().nullable(),
  seasonType: z.string().nullable(),
  gameType: z.string().nullable(),
  /** Null = on the report with no game-day designation. An answer, not a gap. */
  reportStatus: z.string().nullable(),
  reportInjury: z.string().nullable(),
  practiceStatus: z.string().nullable(),
  practiceInjury: z.string().nullable(),
  practiceSecondaryInjury: z.string().nullable(),
});

/**
 * Where a venue is, so somebody can ask what the weather will be there.
 *
 * Curated by us rather than fetched, which is why it is a fact like any other
 * and not a constant in a sport package: it carries a source, an `observed_at`
 * and a trust rank, so the day a real venue database turns up it supersedes
 * this through the ordinary mechanism instead of a code change.
 *
 * `covered` is about the National Weather Service specifically, and it is
 * stored rather than derived because the alternative is asking `api.weather.gov`
 * about Wembley four times a day forever to be told 404 each time.
 */
export const VenueLocation = z.object({
  venueId: z.string().min(1),
  name: z.string().min(1),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  city: z.string().min(1),
  /** A US state code, or a country name where the venue is not in the US. */
  region: z.string().min(1),
  country: z.string().min(1),
  /**
   * Whether the US National Weather Service has a forecast grid for this point.
   *
   * **A deliberate exception to "no vendor word in a field name" (A12), and it
   * keeps the vendor word on purpose.** The honest general field would be
   * "somebody forecasts here", and no such fact exists: coverage is a property
   * of one service's grid, Wembley is outside it, and a venue outside the NWS
   * grid may be perfectly well forecast by somebody else. Renaming this to
   * `forecastCovered` would claim a generality it does not have and would read
   * as false about the eight international venues rather than as silent.
   *
   * When a second weather provider arrives it gets a SIBLING — one boolean per
   * service — and `worklist.ts`, which reads `$.nwsCovered` out of the stored
   * JSON to build the NWS work list, keeps asking the question it means.
   */
  nwsCovered: z.boolean(),
});

/**
 * Which National Weather Service forecast cell a venue sits in.
 *
 * Stored because it never changes and asking is not free. `/points/{lat},{lon}`
 * is a lookup whose answer is fixed for as long as the stadium stands, so
 * holding it turns a per-run cost into a once-per-venue one — the whole reason
 * the NWS fetch is two stages instead of one.
 *
 * **The whole kind is NWS-specific and stays that way (A12).** `gridId`,
 * `gridX` and `gridY` are coordinates in one service's own grid; they mean
 * nothing to any other provider, and there is no generic shape they are a case
 * of. A second weather source gets its own kind beside this one — the fact
 * table is built for exactly that, since two sources asserting about one venue
 * is the ordinary case rather than a conflict. `venue.location` is the
 * provider-neutral half and already exists.
 */
export const VenueGridpoint = z.object({
  venueId: z.string().min(1),
  gridId: z.string().min(1),
  gridX: z.number().int(),
  gridY: z.number().int(),
  timeZone: z.string().nullable(),
  /** The place the service resolved the point to. Our check that it is the right point. */
  city: z.string().nullable(),
  region: z.string().nullable(),
});

/**
 * The weather expected at a kickoff, as last issued.
 *
 * **The grain is the contest, not the issuance.** A forecast is a belief, and
 * this row says what we believe about this kickoff — true from the moment NWS
 * issued it until the moment NWS issued something different, which is exactly
 * what `valid_from`/`valid_to` mean. Keying by `(window, issued-at)` instead
 * would leave both those columns inert, never populate `supersedes`, and make
 * `WHERE valid_to IS NULL` return twenty-eight rows for one game — breaking the
 * read idiom every other consumer uses. `contest.scheduled` settled this shape
 * already: it keys on the game id and lets the drifting moneylines supersede.
 *
 * The hour the forecast is ABOUT is therefore a property of the subject and
 * lives here in the payload, the same way `kickoff` does on the fixture.
 */
export const ForecastKickoff = z.object({
  gameId: z.string().min(1),
  season: z.number().int(),
  period: z.number().int().nullable(),
  venueId: z.string().min(1),
  /** The hour the forecast is about — the kickoff hour, in UTC. */
  windowStart: z.string().min(1),
  windowEnd: z.string().min(1),
  /** When the source issued this forecast. Also the fact's `valid_from`. */
  issuedAt: z.string().min(1),
  /** Normalized at the boundary: the source provides a string like "9 mph". */
  temperatureF: z.number().nullable(),
  windMph: z.number().nullable(),
  /** Present when the source gave a range ("5 to 10 mph"); null when it gave one number. */
  windGustMph: z.number().nullable(),
  windDirection: z.string().nullable(),
  precipitationChance: z.number().min(0).max(100).nullable(),
  relativeHumidity: z.number().min(0).max(100).nullable(),
  dewpointF: z.number().nullable(),
  /** The source's own words, kept verbatim: "Mostly Cloudy". */
  shortForecast: z.string().nullable(),
});

/**
 * One of the source's own fantasy totals, and the scheme it priced it under.
 *
 * A list of these replaced `sourcePoints` and `sourcePointsPpr` on 2026-09-18
 * (A12). The pair was two fields for one idea, and the second of them named a
 * football scoring convention in the SHAPE — so a provider publishing a third
 * scheme, or a second sport publishing one at all, had nowhere to put it
 * without another field and another rename.
 *
 * `scheme` is the source's word, not ours, exactly like `position: 'CB'` — the
 * vocabulary is data, and mapping it to a league's own scoring rules is the
 * job of the layer that knows what a league is. nflverse publishes two,
 * `standard` and `ppr`, and its data dictionary is where those two spellings
 * come from.
 *
 * An absent scheme is a scheme the source did not price, which is why there is
 * no null: `[]` says the source published no fantasy total for this line at
 * all, and the key being required still forces an adapter to answer.
 */
const SourceScore = z.object({
  scheme: z.string().min(1),
  value: z.number().finite(),
});

/**
 * What a player actually did, in one week.
 *
 * **`stats` is an open record on purpose.** The keys are the sport's stat
 * catalog — `pass_yd`, `rec_td`, `fg_50p` — and `packages/info` must never name
 * a sport, so this validates the SHAPE (string keys, finite numbers) and leaves
 * the vocabulary to the sport package that owns it. A football key reaching a
 * basketball league is a question for the layer that knows what a league is.
 *
 * **A `stat.line` carries only its NON-ZERO stats, and an absent key is zero.**
 * That is the opposite of this project's usual rule and it is deliberate: the
 * rule exists to protect against a source that can be SILENT, and the source
 * here never is. nflverse fills every stat column on every row — measured on
 * the 2026 weekly file, not one blank cell — so absence carries no information
 * the value does not, while 95% of the values are zero.
 *
 * The condition is load-bearing, so state it: **an adapter whose source
 * publishes some stat categories and not others must not emit this kind**
 * without extending it to say which categories it covered. Otherwise an
 * unpublished defensive line reads as a defense that did nothing.
 *
 * `sourcePoints` is the provider's own fantasy totals, carried as a second
 * opinion for catching a drifting scoring engine. They are never a league's
 * score: rules are data, every league prices its own, and rendering somebody
 * else's total as ours would be the one number on the page nobody could
 * reproduce.
 */
export const StatLine = z.object({
  ...PersonWeek,
  seasonType: z.string().nullable(),
  position: z.string().nullable(),
  opponent: z.string().nullable(),
  gameId: z.string().nullable(),
  stats: z.record(z.string(), z.number().finite()),
  sourcePoints: z.array(SourceScore),
});

/**
 * What a club's defense and special teams did, in one game.
 *
 * ITS OWN KIND, NOT A `stat.line` WITH A TEAM IN THE PERSON SLOT. A person line
 * is keyed by `personKey` and a reader asking for one never wants a club; a
 * club line has a different grain (one per club per game, never per player)
 * and a different subject type (`team`). ICD-S07 §13 says a breaking change to
 * a kind mints a new one, and this is the honest version of that. The subject
 * edge points at the `team` entity the schedules already assert — never at a
 * pseudo-person, which the Info Manager plan refused on 2026-09-12.
 *
 * `stats` follows every rule `stat.line` sets: our keys, non-zero values only,
 * and the same load-bearing condition — an adapter whose source covers some
 * categories and not others must not emit this kind. Two keys a defense is
 * scored on are NOT the source's own row: yards allowed is the OPPONENT's net
 * yardage from the same file, and points allowed is the opponent's final score
 * from `contest.result`, which the projection joins on `gameId`. See
 * docs/design/DST.md.
 */
export const TeamStatLine = z.object({
  /** The source's club code, verbatim: `KC`, `LA`. */
  teamKey: z.string().min(1),
  season: z.number().int(),
  /** The source's week. Readers key by `gameId`, never by this (see stats.ts). */
  period: z.number().int(),
  seasonType: z.string().nullable(),
  opponent: z.string().nullable(),
  gameId: z.string().nullable(),
  stats: z.record(z.string(), z.number().finite()),
  /** `[]` when the source prices no team-defense scheme, which nflverse does not. */
  sourcePoints: z.array(SourceScore),
});

/**
 * Where a player sits on the club's published chart.
 *
 * **`rank` is the answer.** 1 is the starter. `roster.membership.depthPosition`
 * looks like it should say this and never can — it is a finer POSITION (CB and
 * FS are both DB), not an ordering.
 *
 * The grain is the player's place within ONE formation group, because a player
 * legitimately appears in several: a receiver ranks in "3WR 1TE" and again in
 * "4WR", and those are different answers to different questions.
 *
 * The chart's own timestamp is the fact's `validFrom`, not a payload field.
 * Until 2026-10-07 it was also `capturedAt` here, which moved every day and
 * made every unchanged place a revision; rows written before then still carry
 * it, and parse, because an object schema drops a key it does not name.
 */
export const DepthPosition = z.object({
  personKey: z.string().min(1),
  season: z.number().int(),
  team: z.string().min(1),
  rank: z.number().int(),
  slot: z.number().int().nullable(),
  position: z.string().nullable(),
  positionName: z.string().nullable(),
  positionGroup: z.string().nullable(),
  positionGroupId: z.string().min(1),
  positionId: z.string().min(1),
});

/**
 * Every kind that may be written, and the schema it must satisfy.
 *
 * Every kind the nflverse manifest declares in `emits` is now registered. A
 * manifest may declare one before it is registered — declaring an intention is
 * not the same as being allowed to write — and `validateFact` is what enforces
 * the difference. A manifest may declare a kind in `emits` before it
 * is registered — declaring an intention is not the same as being allowed to
 * write, and `validateFact` is what enforces the difference.
 */
export const KINDS = {
  'contest.scheduled': ContestScheduled,
  'contest.result': ContestResult,
  'person.profile': PersonProfile,
  'roster.membership': RosterMembership,
  'injury.report': InjuryReport,
  'venue.location': VenueLocation,
  'venue.gridpoint': VenueGridpoint,
  'forecast.kickoff': ForecastKickoff,
  'stat.line': StatLine,
  'team.stat.line': TeamStatLine,
  'depth.position': DepthPosition,
} as const;

export type KnownKind = keyof typeof KINDS;

export function isKnownKind(kind: string): kind is KnownKind {
  return Object.prototype.hasOwnProperty.call(KINDS, kind);
}

export type FactValidation =
  { ok: true; kind: KnownKind; payload: unknown } | { ok: false; reason: string };

/**
 * Check one payload against its kind.
 *
 * An unregistered kind is refused rather than waved through. The alternative —
 * storing anything whose kind we do not recognize — means the first time a
 * typo'd kind is written, it is written forever and nothing notices.
 */
export function validateFact(kind: string, payload: unknown): FactValidation {
  if (!isKnownKind(kind)) {
    return {
      ok: false,
      reason: `unregistered fact kind "${kind}". Register a schema in packages/info/src/kinds/registry.ts before emitting it.`,
    };
  }
  const parsed = KINDS[kind].safeParse(payload);
  if (!parsed.success) {
    const detail = parsed.error.issues
      .slice(0, 5)
      .map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`)
      .join('; ');
    return { ok: false, reason: `payload does not match ${kind} — ${detail}` };
  }
  return { ok: true, kind, payload: parsed.data };
}

/** The topics a fact may carry. A taxonomy, not free text. */
export const TOPICS = [
  'injury',
  'legal',
  'transaction',
  'performance',
  'weather',
  'suspension',
  'milestone',
] as const;

export type Topic = (typeof TOPICS)[number];

export function isKnownTopic(topic: string): topic is Topic {
  return (TOPICS as readonly string[]).includes(topic);
}

/**
 * Roles a fact-to-entity edge may carry.
 *
 * The role is what makes "everything about Mahomes" a dossier rather than four
 * thousand rows of noise, so the vocabulary is closed. Adding one is a design
 * decision about what a caller can ask for.
 */
export const ROLES = [
  'subject',
  'participant',
  'opponent',
  'venue',
  'employer',
  'mentions',
] as const;

export type Role = (typeof ROLES)[number];

export function isKnownRole(role: string): role is Role {
  return (ROLES as readonly string[]).includes(role);
}
